import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Wand2, Lightbulb, Rocket, Sparkles, Binary, Boxes } from 'lucide-react'
import SEO from '../../components/SEO'

const TechnologyMagic = () => {
  const features = [
    {
      icon: Wand2,
      title: 'Breakthrough Innovation',
      description: 'Creating solutions that push boundaries and redefine what\'s possible with technology.'
    },
    {
      icon: Lightbulb,
      title: 'Visionary Thinking',
      description: 'Transforming ambitious ideas into reality with cutting-edge technology and expertise.'
    },
    {
      icon: Rocket,
      title: 'First-of-Its-Kind',
      description: 'Pioneering unique capabilities and experiences that have never existed before.'
    },
    {
      icon: Sparkles,
      title: 'Seamless Integration',
      description: 'Making complex technology feel effortless and intuitive, like magic.'
    },
    {
      icon: Binary,
      title: 'Custom Engineering',
      description: 'Bespoke software and systems engineered specifically for your unique requirements.'
    },
    {
      icon: Boxes,
      title: 'Impossible Made Possible',
      description: 'Solving challenges others say can\'t be done with innovative approaches and solutions.'
    }
  ]

  const examples = [
    {
      title: 'Intelligent Home Orchestration',
      description: 'Seamlessly controlling your entire estate—lights, climate, security, entertainment—across multiple properties with a single voice command or gesture.'
    },
    {
      title: 'Predictive Personal Assistant',
      description: 'AI-powered systems that anticipate your needs before you ask, preparing your environment and resources based on your patterns and preferences.'
    },
    {
      title: 'Unified Communication Hub',
      description: 'Consolidating all communication channels—phone, email, messaging, video—into one intelligent interface that knows when and how you prefer to be reached.'
    },
    {
      title: 'Global Connectivity Suite',
      description: 'Uninterrupted secure connectivity whether you\'re in your home, on your yacht, or traveling via private jet with seamless handoffs.'
    },
    {
      title: 'Privacy-First Digital Identity',
      description: 'Advanced identity management that protects your privacy while enabling frictionless access to your digital life.'
    },
    {
      title: 'Proactive Security Shield',
      description: 'AI-driven security that learns and adapts, protecting you from threats before they materialize across all your digital touchpoints.'
    }
  ]

  return (
    <>
      <SEO 
        title="Technology Magic - Praetorian Works"
        description="Creating breakthrough technology solutions that seem like magic. Custom engineering, innovative capabilities, and first-of-its-kind experiences for discerning clients."
        keywords="innovative technology, custom software, breakthrough solutions, bespoke technology, technology innovation, cutting-edge IT"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary-900/20 via-transparent to-transparent"></div>
          
          <div className="container-max relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl mb-8 shadow-2xl">
                <Wand2 className="w-10 h-10 text-secondary-900" />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Technology
                <span className="text-primary-500 ml-4">Magic</span>
              </h1>
              <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
                We create technology experiences so advanced, so seamless, so perfectly tailored 
                to you that they feel like magic. Pioneering solutions that don't just meet needs—they 
                anticipate them and exceed your wildest expectations.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20">
          <div className="container-max">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  className="bg-gradient-to-br from-secondary-800 to-black rounded-2xl p-8 border border-neutral-800 hover:border-primary-500/50 transition-all duration-300 group"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl mb-6 shadow-lg group-hover:shadow-primary-500/50 transition-shadow duration-300">
                    <feature.icon className="w-8 h-8 text-secondary-900" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Examples */}
        <section className="py-20 bg-black/30">
          <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">
                What's <span className="text-primary-500">Possible</span>
              </h2>
              <p className="text-xl text-neutral-300 mb-16 text-center leading-relaxed">
                Technology Magic turns ambitious visions into reality. Here are just a few examples 
                of the extraordinary experiences we've created:
              </p>
              
              <div className="grid md:grid-cols-2 gap-8">
                {examples.map((example, index) => (
                  <motion.div
                    key={example.title}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.1 * index }}
                    className="bg-secondary-900 rounded-xl p-8 border border-neutral-800 hover:border-primary-500/30 transition-all duration-300"
                  >
                    <div className="w-3 h-3 bg-primary-500 rounded-full mb-4"></div>
                    <h3 className="text-2xl font-bold text-white mb-4">
                      {example.title}
                    </h3>
                    <p className="text-neutral-400 text-lg leading-relaxed">
                      {example.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Philosophy */}
        <section className="py-20">
          <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                The <span className="text-primary-500">Magic</span> is in the Details
              </h2>
              <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
                Technology Magic isn't about smoke and mirrors—it's about deep expertise, 
                innovative thinking, and meticulous execution. We combine cutting-edge technology 
                with profound understanding of your needs to create experiences that truly wow.
              </p>
              <p className="text-lg text-neutral-400 leading-relaxed">
                Every solution is custom-crafted, every detail perfected, every interaction 
                designed to feel effortless. That's the essence of Technology Magic.
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black/30">
          <div className="container-max text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Let's Create Magic Together
              </h2>
              <p className="text-xl text-neutral-300 mb-8 max-w-2xl mx-auto">
                Have an ambitious vision? Ready to create something extraordinary? 
                Let's explore what's possible.
              </p>
              <Link to="/contact" className="btn-primary text-lg">
                Start the Conversation
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default TechnologyMagic
